// src/app/services/router-state-tracker.service.ts
import { Injectable, OnInit } from '@angular/core';
import { Router, Event as RouterEvent, NavigationStart } from '@angular/router';
import { NavController } from '@ionic/angular'; // Import NavController
import { ApiService } from '../provider/api.service';
import { endpoints } from 'src/app/constant/endpoints';
import { ConfigService } from '../provider/config.service';
import { Events } from 'src/app/provider/events.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnInit {
  private headerSize = new BehaviorSubject<string>('bigHead'); // Set an initial value
  currentheaderSize: Observable<string> = this.headerSize.asObservable();

  shadowConfigs = {
    // Core shadows - More punchy and defined
    m: [
      { x: 0, y: 1, blur: 2, opacity: 0.12 },
      { x: 1, y: 3, blur: 6, spread: -1, opacity: 0.15 },
      { x: 1, y: 6, blur: 12, spread: -2, opacity: 0.12 },
    ],
    l: [
      { x: 0, y: 1, blur: 2, opacity: 0.1 },
      { x: 1, y: 2, blur: 4, spread: -1, opacity: 0.12 },
    ],
    h: [
      { x: 0, y: 3, blur: 6, spread: -1, opacity: 0.15 },
      { x: 1, y: 6, blur: 12, spread: -2, opacity: 0.2 },
      { x: 1, y: 12, blur: 24, spread: -3, opacity: 0.2 },
    ],
    h2: [
      // Dramatic high shadow
      { x: 0, y: 4, blur: 8, spread: -1, opacity: 0.18 },
      { x: 1, y: 8, blur: 16, spread: -2, opacity: 0.15 },
      { x: 1, y: 16, blur: 32, spread: -4, opacity: 0.12 },
    ],

    // Elevation shadows - Crisper and more defined
    'elevation-low': [
      { x: 0, y: 1, blur: 2, opacity: 0.15 },
      { x: 1, y: 2, blur: 4, spread: -1, opacity: 0.12 },
    ],
    'elevation-medium': [
      { x: 0, y: 2, blur: 4, opacity: 0.15 },
      { x: 1, y: 4, blur: 8, spread: -1, opacity: 0.18 },
      { x: 1, y: 8, blur: 16, spread: -2, opacity: 0.15 },
    ],
    'elevation-high': [
      { x: 0, y: 4, blur: 8, spread: -1, opacity: 0.2 },
      { x: 1, y: 8, blur: 16, spread: -2, opacity: 0.22 },
      { x: 1, y: 16, blur: 32, spread: -4, opacity: 0.18 },
    ],

    // Diffuse shadows - More noticeable ambient effect
    diffuse: [
      { x: 0, y: 0, blur: 4, spread: 0, opacity: 0.08 },
      { x: 1, y: 2, blur: 8, spread: -1, opacity: 0.1 },
      { x: 1, y: 4, blur: 16, spread: -2, opacity: 0.12 },
    ],
    'color-s': [
      // Enhanced colored special diffuse
      { x: 0, y: 0, blur: 8, spread: 2, opacity: 0.08 },
      { x: 1, y: 4, blur: 16, spread: -1, opacity: 0.12 },
      { x: 1, y: 8, blur: 24, spread: -2, opacity: 0.15 },
    ],

    // Inner shadows - Stronger depth effect
    l1: [
      { x: 0, y: 1, blur: 2, opacity: 0.15, inset: true },
      { x: 1, y: 2, blur: 4, spread: -1, opacity: 0.2, inset: true },
    ],
    l2: [
      { x: 0, y: 2, blur: 4, opacity: 0.2, inset: true },
      { x: 1, y: 4, blur: 8, spread: 1, opacity: 0.25, inset: true },
    ],
    l3: [
      { x: 0, y: 1, blur: 12, spread: 1, opacity: 0.05, inset: true },
      { x: 1, y: 2, blur: 20, spread: -4, opacity: 0.1, inset: true },
    ],
    inner: [
      { x: 1, y: -1, blur: 2, spread: 2, opacity: 0.1, inset: true },
      { x: 1, y: 1, blur: 4, spread: 1, opacity: 0.2, inset: true },
    ],
    inner2: [
      { x: 0, y: 0, blur: 1, spread: 1, opacity: 0.1, inset: true },
      { x: 0, y: 0, blur: 0, spread: 1, opacity: 0.3, inset: true },
    ],
    inner3: [
      { x: 0, y: 0, blur: 1, spread: 0, opacity: 0.1, inset: true },
      { x: 0, y: 0, blur: 0, spread: 0, opacity: 0.1, inset: true },
    ],
    l4: [
      { x: 0, y: 4, blur: 8, opacity: 0.25, inset: true },
      { x: 1, y: 8, blur: 16, spread: 2, opacity: 0.3, inset: true },
    ],

    // Card shadows - Sharper and more defined
    card: [
      { x: 0, y: 1, blur: 2, spread: 0, opacity: 0.12 },
      { x: 1, y: 2, blur: 4, spread: -1, opacity: 0.15 },
      { x: 1, y: 4, blur: 8, spread: -2, opacity: 0.12 },
    ],
    'm-c': [
      // Colored medium with better contrast
      { x: 0, y: 2, blur: 4, spread: -1, opacity: 0.15 },
      { x: 1, y: 4, blur: 8, spread: -2, opacity: 0.18 },
      { x: 1, y: 8, blur: 16, spread: -3, opacity: 0.15 },
    ],

    // Directional shadows - More pronounced
    side: [
      { x: 1, y: 1, blur: 4, spread: -1, opacity: 0.12 },
      { x: 1.5, y: 1.5, blur: 8, spread: -2, opacity: 0.15 },
    ],
    'm-side': [
      { x: 3, y: 5, blur: 10, spread: -1, opacity: 0.05 },
      { x: 5, y: 3, blur: 10, spread: -2, opacity: 0.1 },
    ],
    'side-anim': [
      { x: 3, y: 5, blur: 10, spread: -1, opacity: 0.05 },
      { x: 5, y: 3, blur: 10, spread: -2, opacity: 0.1 },
    ],
    'side-anim-end': [
      { x: 9, y: 15, blur: 10, spread: -1, opacity: 0.15 },
      { x: 18, y: 30, blur: 10, spread: -2, opacity: 0.1 },
    ],
    'minor-anim': [
      { x: 2, y: 2, blur: 5, spread: 0, opacity: 0.05 },
      { x: -2, y: -2, blur: 10, spread: 0, opacity: 0.01 },
    ],
    'minor-anim-end': [
      { x: 4, y: 4, blur: 5, spread: 0, opacity: 0.1 },
      { x: -5, y: -5, blur: 10, spread: 0, opacity: 0.01 },
    ],
    'btn-anim': [
      { x: 1.5, y: 2.5, blur: 7, spread: -0.5, opacity: 0.05 },
      { x: 2.5, y: 1.5, blur: 7, spread: -1, opacity: 0.1 },
    ],
    'btn-anim-end': [
      { x: 4.5, y: 7.5, blur: 6, spread: -0.5, opacity: 0.2 },
      { x: 9, y: 15, blur: 6, spread: -1, opacity: 0.1 },
    ],
    'option-anim': [
      { x: 1, y: 2, blur: 5, spread: -0.5, opacity: 0.05 },
      { x: 2, y: 1, blur: 5, spread: -1, opacity: 0.1 },
    ],
    'option-anim-end': [
      { x: 3, y: 2, blur: 6, spread: -0.5, opacity: 0.2 },
      { x: 9, y: 1, blur: 6, spread: -1, opacity: 0.1 },
    ],
    'glass-anim': [
      { x: 2, y: 2.5, blur: 5, spread: -2.5, opacity: 0.1 },
      { x: 3, y: 3.7, blur: 5, spread: -2.5, opacity: 0.1 },
    ],
    'glass-anim-end': [
      { x: 4, y: 5, blur: 9, spread: -2.5, opacity: 0.1 },
      { x: 6, y: 7, blur: 9, spread: -2.5, opacity: 0.2 },
    ],

    // Special effects - Enhanced visibility
    'm-anim': [
      // Animation-friendly with better presence
      { x: 0, y: 2, blur: 4, spread: -1, opacity: 0.12 },
      { x: 1, y: 4, blur: 8, spread: -2, opacity: 0.15 },
      { x: 1, y: 8, blur: 16, spread: -3, opacity: 0.12 },
    ],
    'm-small': [
      // Subtle but visible
      { x: 0, y: 1, blur: 2, opacity: 0.1 },
      { x: 1, y: 2, blur: 4, spread: -1, opacity: 0.12 },
      { x: 1, y: 3, blur: 6, spread: -1, opacity: 0.1 },
    ],
    md: [
      // Dense medium with better definition
      { x: 0, y: 1, blur: 2, opacity: 0.15 },
      { x: 1, y: 2, blur: 4, spread: -1, opacity: 0.2 },
    ],
    mid: [
      // Dense medium with better definition
      { x: 0, y: 1, blur: 2, opacity: 0.15 },
      { x: 1, y: 2, blur: 4, spread: -1, opacity: 0.2 },
    ],
  };
  colors = {
    primary: '#35858b',
    secondary: '#4fbdba',
    tertiary: '#082227',
    on_primary: '#35858b',
    on_secondary: '#4fbdba',
    on_tertiary: '#082227',
  };
  macros: any = {};
  isPageLoaded: boolean = false;
  isOnlineOrders: boolean = false;
  safeAreaInsets: any = {};
  masterColorArray: any = [];
  settings: any = {};
  private appSettingsSubscription: Subscription | undefined; // For ConfigService updates
  configSub: Subscription | undefined;
  configs: any = {};
  appPayload: any;
  macroColors: any = [];
  constructor(private router: Router, private navCtrl: NavController, private apiService: ApiService, private configService: ConfigService, private events: Events, private dataService: DataService) {}
  async ngOnInit() {
    await this.getData();
  }

  async getData() {
    this.appSettingsSubscription = this.configService.appSettings$.subscribe((currentSettings) => {
      this.settings = currentSettings;
    });

    this.configSub = this.dataService.getData('configurations').subscribe((response) => {
      if (response) {
        this.configs = response.organization;

        this.macroColors = [];

        this.macroColors.push({
          calorie: this.configs.configurations.theme.on_primary_container,
          protein: this.configs.configurations.theme.on_secondary_container,
          carb: this.configs.configurations.theme.on_tertiary_container,
          fat: this.configs.configurations.theme.on_error_container,
        });

        // Assuming 'response' is the object containing your theme data.
        // Ensure 'this.masterColorArray' is initialized as an empty array before these pushes.
        this.masterColorArray = [];

        this.masterColorArray.push({
          name: 'light', // You can choose more descriptive names here
          primary: this.configs.configurations.theme.primary,
          secondary: this.configs.configurations.theme.secondary,
          tertiary: this.configs.configurations.theme.tertiary,
        });

        this.masterColorArray.push({
          name: 'dark',
          primary: this.configs.configurations.theme.on_primary,
          secondary: this.configs.configurations.theme.on_secondary,
          tertiary: this.configs.configurations.theme.on_tertiary,
        });

        this.masterColorArray.push({
          name: 'light2',
          primary: this.configs.configurations.theme.primary_container,
          secondary: this.configs.configurations.theme.secondary_container,
          tertiary: this.configs.configurations.theme.tertiary_container,
        });

        this.masterColorArray.push({
          name: 'dark2',
          primary: this.configs.configurations.theme.surface_variant,
          secondary: this.configs.configurations.theme.on_surface_variant,
          tertiary: this.configs.configurations.theme.outline,
        });

        this.masterColorArray.push({
          name: 'light3',
          primary: this.configs.configurations.theme.error,
          secondary: this.configs.configurations.theme.on_error,
          tertiary: this.configs.configurations.theme.error_container,
        });

        this.masterColorArray.push({
          name: 'dark3',
          primary: this.configs.configurations.theme.background,
          secondary: this.configs.configurations.theme.on_background,
          tertiary: this.configs.configurations.theme.surface,
        });
      }
      this.setTheme('light');
      this.isOnlineOrders = this.configs.show_online_orders === 'Yes';
      this.configService.updateConfigurations(this.configs);
      this.appPayload = this.configService.configurationsSync();
      this.events.publish('payload:configuration', {
        appPayload: this.appPayload,
        time: new Date(),
      });
    });
  }
  ngOnDestroy() {
    if (this.appSettingsSubscription) {
      this.appSettingsSubscription.unsubscribe();
    }
    if (this.configSub) {
      this.configSub.unsubscribe();
    }
  }
  async initialSetup(theme) {
    await this.getData();
    this.setTheme(theme);
  }
  themeSwitch(paletteName: string) {
    for (const palette of this.masterColorArray) {
      if (palette.name === paletteName) {
        this.theColorUpdate(palette.primary, palette.secondary, palette.tertiary);
        // this.setTheme(paletteName);
      }
    }
  }
  setTheme(eve: string) {
    const mode = eve ? eve : 'light';
    this.settings.setTheme = mode;
    this.themeSwitch(mode);
    this.configService.updateAppSettings({ setTheme: mode });
    this.events.publish('setTheme:change', this.configService.currentSettings.setTheme);
  }
  updateHeaderSize(value: string) {
    this.headerSize.next(value);
  }
  hexToHsl(hex) {
    hex = hex.replace(/^#/, '');

    let r = 0,
      g = 0,
      b = 0;

    // Check for shorthand hex (e.g., #abc) and expand it
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      // Full hex (e.g., #aabbcc)
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    } else {
      throw new Error('Invalid HEX color format');
    }

    // Convert RGB values to a range of 0 to 1
    r /= 255;
    g /= 255;
    b /= 255;

    // Find the max and min values of RGB
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    // Calculate Lightness
    let l = (max + min) / 2;

    let h = 0,
      s = 0;

    if (delta !== 0) {
      // Calculate Hue and Saturation only if there's no gray (delta = 0)
      // Saturation
      s = delta / (1 - Math.abs(2 * l - 1));

      // Hue
      if (max === r) {
        h = ((g - b) / delta + (g < b ? 6 : 0)) % 6;
      } else if (max === g) {
        h = (b - r) / delta + 2;
      } else if (max === b) {
        h = (r - g) / delta + 4;
      }

      h *= 60; // Convert to degrees
    }

    // Convert to percentages
    h = Math.round(h);
    s = Math.round(s * 100);
    l = Math.round(l * 100);

    return {
      h,
      s,
      l,
    };
  }
  invertHsl(hsl) {
    let h = (hsl.h + 180) % 360;
    let l = hsl.l < 50 ? hsl.l + 30 : hsl.l - 30;
    return {
      h: h,
      s: hsl.s,
      l: l,
      alpha: 1,
    };
  }
  dynamicText(hsl) {
    let l;
    if (hsl.l <= 49) {
      l = 100;
    } else if (hsl.l >= 50 && hsl.l <= 80) {
      l = 10;
    } else if (hsl.l > 80 && hsl.l <= 90) {
      // Corrected logic
      l = 15;
    } else {
      l = 0;
    }

    const safeHSL = {
      h: 0,
      s: 0,
      l: l,
      alpha: hsl?.alpha ?? 1, // Use hsl.alpha if defined, otherwise default to 1
    };
    return safeHSL;
  }
  setColorVariables(htmlEl, prefix, baseHSL, shades) {
    Object.keys(shades).forEach((shade) => {
      // Get the modified HSL values
      const modifiedHSL = shades[shade]({
        ...baseHSL,
      });

      // Set the traditional variable with opacity included
      htmlEl.style.setProperty(`--ion-${shade}-${prefix}`, this.finalHSL(modifiedHSL));

      // Set a new color-only variable without opacity
      htmlEl.style.setProperty(`--drop-${shade}-${prefix}`, `${modifiedHSL.h}deg ${modifiedHSL.s}% ${modifiedHSL.l}%`);
    });
  }
    setColorGloss(htmlEl, prefix, baseHSL, shades) {
    Object.keys(shades).forEach((shade) => {
      // Get the modified HSL values
      const modifiedHSL = shades[shade]({
        ...baseHSL,
      });

      // Set the traditional variable with opacity included
      htmlEl.style.setProperty(`--ion-${shade}-${prefix}`, modifiedHSL);

      // Set a new color-only variable without opacity
      // htmlEl.style.setProperty(`--drop-${shade}-${prefix}`, `${modifiedHSL.h}deg ${modifiedHSL.s}% ${modifiedHSL.l}%`);
    });
  }
  generateShadowString(shadowConfig, hsl) {
    return shadowConfig
      .map((shadow) => {
        let spreadStr = shadow.spread ? ` ${shadow.spread}px` : '';
        let insetStr = shadow.inset ? ' inset' : '';
        return `${shadow.x}px ${shadow.y}px ${shadow.blur}px${spreadStr} hsl(${hsl.h}deg ${hsl.s}% ${hsl.l}% / ${shadow.opacity})${insetStr}`;
      })
      .join(', ');
  }
  setShadowVariables(htmlEl, prefix, baseHSL) {
    // Create light and dark versions of the base color for shadows
    let shadowLight = this.hslModify(baseHSL, {
      l: {
        fixed: 25,
      },
      s: {
        percent: -10,
      },
    });

    let shadowDark = this.hslModify(baseHSL, {
      l: {
        fixed: 10,
      },
      s: {
        percent: 60,
      },
    });

    let shadowColor = this.hslModify(baseHSL, {
      opacity: 1,
    });
    let shadowColor2 = this.hslModify(baseHSL, {
      opacity: 0.5,
    });
    let shadowMain = this.hslModify(baseHSL, {
      l: {
        fixed: 5,
      },
      s: {
        fixed: 0,
      },
    });
    let shadowGrey = this.hslModify(baseHSL, {
      l: { l: Math.min(Math.max(baseHSL.l * 0.5, 10), 40) },
      s: { s: Math.min(Math.max(baseHSL.s * 0.6, 40), 100) },
      h: {
        h: baseHSL.h < 180 ? (baseHSL.h + 60) % 360 : (baseHSL.h - 60 + 360) % 360,
      },
      opacity: 1,
    });

    // Set base shadow variables
    htmlEl.style.setProperty(`--shadow-light`, `${shadowLight.h}deg ${shadowLight.s}% ${shadowLight.l}%`);
    htmlEl.style.setProperty(`--shadow-dark`, `${shadowDark.h}deg ${shadowDark.s}% ${shadowDark.l}%`);

    // Generate all shadow variations
    Object.entries(this.shadowConfigs).forEach(([key, config]) => {
      htmlEl.style.setProperty(`--shadow-${key}`, this.generateShadowString(config, shadowMain));

      // Also create a dark version
      htmlEl.style.setProperty(`--shadow-${key}-dark`, this.generateShadowString(config, shadowDark));
      htmlEl.style.setProperty(`--shadow-${key}-grey`, this.generateShadowString(config, shadowGrey));
      htmlEl.style.setProperty(`--shadow-${key}-light`, this.generateShadowString(config, shadowLight));
      htmlEl.style.setProperty(`--shadow-${key}-color`, this.generateShadowString(config, shadowColor));
    });
  }
  theColorUpdate(primary, secondary, tertiary) {
    const htmlEl = document.documentElement; // Get the root HTML element
    if (!htmlEl) return;

    const primaryHSL = this.hexToHsl(primary);
    const secondaryHSL = this.hexToHsl(secondary);
    const tertiaryHSL = this.hexToHsl(tertiary);
    const greyHSL = this.hexToHsl('#c1c1ff');
    const dangerHSL = this.hexToHsl('#d32f2f'); // Soft red (danger)
    const successHSL = this.hexToHsl('#388e3c'); // Balanced green (success)
    const warningHSL = this.hexToHsl('#f57c00'); // Slightly muted orange (warning)
    const infoHSL = this.hexToHsl('#1976d2'); // Standard info blue
    const maleHSL = this.hexToHsl('#1565c0'); // Deep, bold blue
    const femaleHSL = this.hexToHsl('#8e24aa'); // Elegant purple
    const healthHSL = this.hexToHsl('#2e7d32'); // Strong green (wellness)
    const unhealthHSL = this.hexToHsl('#81bcb5');
    const whiteHSL = this.hexToHsl('#fff');
    const blackHSL = this.hexToHsl('#000'); // Deep red (opposite to health)
    const calorieHsl = this.hexToHsl(this.macroColors[0].calorie);
    const proteinHsl = this.hexToHsl(this.macroColors[0].protein);
    const carbHsl = this.hexToHsl(this.macroColors[0].carb);
    const fatHsl = this.hexToHsl(this.macroColors[0].fat);
    let gloss = {
      glass: (hsl) => this.simpleh(hsl),
      glow: (hsl) => this.simples(hsl),
      satur: (hsl) => this.simplel(hsl),
    }
    let shades = {
      color: (hsl) => this.hslModify(hsl, { opacity: 1 }),

      black: (hsl) =>
        this.hslModify(hsl, {
          l: { fixed: 2 },
          opacity: 1,
        }),
      white: (hsl) =>
        this.hslModify(hsl, {
          l: { fixed: 100 },
          opacity: 1,
        }),

      pass: (hsl) =>
        this.hslModify(hsl, {
          opacity: 0.05,
        }),
      ultra: (hsl) =>
        this.hslModify(hsl, {
          opacity: 0.15,
        }),
      fade: (hsl) =>
        this.hslModify(hsl, {
          opacity: 0.25,
        }),
      less: (hsl) =>
        this.hslModify(hsl, {
          opacity: 0.35,
        }),
      clear: (hsl) =>
        this.hslModify(hsl, {
          opacity: 0.5,
        }),
      half: (hsl) =>
        this.hslModify(hsl, {
          opacity: 0.75,
        }),
      more: (hsl) =>
        this.hslModify(hsl, {
          opacity: 0.85,
        }),

      shadeLight: (hsl) =>
        this.hslModify(hsl, {
          l: { l: Math.min(Math.max(hsl.l * 1.1, 10), 90) },
          s: { s: Math.min(Math.max(hsl.s * 0.9, 10), 100) },
          opacity: 1,
        }),

      shadeDark: (hsl) =>
        this.hslModify(hsl, {
          l: { l: Math.min(Math.max(hsl.l * 0.9, 10), 90) },
          s: { s: Math.min(Math.max(hsl.s * 1.1, 10), 100) },
          opacity: 1,
        }),

      light: (hsl) =>
        this.hslModify(hsl, {
          l: { l: Math.min(Math.max(hsl.l * 2.3, 10), 70) },
          s: { s: Math.min(Math.max(hsl.s * 0.3, 10), 80) },
          opacity: 1,
        }),

      dark: (hsl) =>
        this.hslModify(hsl, {
          l: { l: Math.min(Math.max(hsl.l * 0.3, 25), 35) },
          s: { s: Math.min(Math.max(hsl.s * 1.3, 20), 90) },
          opacity: 1,
        }),

      lighter: (hsl) =>
        this.hslModify(hsl, {
          l: { l: Math.min(Math.max(hsl.l * 2.3, 10), 80) },
          s: { s: Math.min(Math.max(hsl.s * 0.5, 10), 100) },
          opacity: 1,
        }),

      darker: (hsl) =>
        this.hslModify(hsl, {
          l: { l: 10 },
          s: { s: Math.min(Math.max(hsl.s * 1.5, 10), 95) },
          opacity: 1,
        }),

      bright: (hsl) =>
        this.hslModify(hsl, {
          l: { l: Math.min(Math.max(hsl.s * 1.6, 10), 90) },
          s: { s: Math.min(Math.max(hsl.s * 1.2, 10), 90) },
          opacity: 1,
        }),
      washed: (hsl) =>
        this.hslModify(hsl, {
          s: { s: Math.min(Math.max(hsl.s * 0.5, 10), 50) },
          l: { l: 88 },
          opacity: 1,
        }),
      bareLight: (hsl) =>
        this.hslModify(hsl, {
          s: { s: Math.min(Math.max(hsl.s * 0.5, 10), 80) },
          l: { l: 95 },
          opacity: 1,
        }),
      bareDark: (hsl) =>
        this.hslModify(hsl, {
          s: { s: Math.min(Math.max(hsl.s * 0.5, 10), 70) },
          l: { l: 15 },
          opacity: 1,
        }),
      highlight: (hsl) =>
        this.hslModify(hsl, {
          l: {
            l: hsl.l > 60 ? Math.min(Math.max(hsl.l - 10, 20), 80) : Math.min(Math.max(hsl.l + 15, 10), 90),
          },
          h: { h: (hsl.h + 5) % 360 },
          s: { s: Math.min(Math.max(hsl.s * 1.25, 10), 90) },
          opacity: 1,
        }),
      dull: (hsl) =>
        this.hslModify(hsl, {
          l: { l: Math.min(Math.max(hsl.s * 1, 10), 45) },
          s: { s: Math.min(Math.max(hsl.s * 0.95, 30), 80) },
          opacity: 1,
        }),
      cool: (hsl) =>
        this.hslModify(hsl, {
          s: { s: Math.min(hsl.s - 10, 70) },
          h: { h: (hsl.h + 15) % 360 },
          opacity: 1,
        }),
      warm: (hsl) =>
        this.hslModify(hsl, {
          s: { s: Math.min(hsl.s + 10, 70) },
          h: { h: (hsl.h - 15 + 360) % 360 },
          opacity: 1,
        }),
      diffuseLight: (hsl) =>
        this.hslModify(hsl, {
          l: { l: 95 },
          s: { s: Math.min(Math.max(hsl.s * 0.75, 30), 80) },
          opacity: 0.7,
        }),
      diffuseDark: (hsl) =>
        this.hslModify(hsl, {
          l: { l: 3 },
          opacity: 0.7,
        }),

      text: (hsl) => this.dynamicText(hsl),
      invert: (hsl) => this.invertHsl(hsl),
    };
this.setColorGloss(
      htmlEl,
      'primary',
      {
        ...primaryHSL,
      },
      gloss,
    );
    this.setColorGloss(
      htmlEl,
      'secondary',
      {
        ...primaryHSL,
      },
      gloss,
    );
    this.setColorGloss(
      htmlEl,
      'tertiary',
      {
        ...primaryHSL,
      },
      gloss,
    );
    this.setColorVariables(
      htmlEl,
      'primary',
      {
        ...primaryHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'secondary',
      {
        ...secondaryHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'tertiary',
      {
        ...tertiaryHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'grey',
      {
        ...greyHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'danger',
      {
        ...dangerHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'success',
      {
        ...successHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'warning',
      {
        ...warningHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'info',
      {
        ...infoHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'male',
      {
        ...maleHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'female',
      {
        ...femaleHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'health',
      {
        ...healthHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'secondaryPro',
      {
        ...unhealthHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'white',
      {
        ...whiteHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'black',
      {
        ...blackHSL,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'calorie',
      {
        ...calorieHsl,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'prot',
      {
        ...proteinHsl,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'fat',
      {
        ...fatHsl,
      },
      shades,
    );
    this.setColorVariables(
      htmlEl,
      'carb',
      {
        ...carbHsl,
      },
      shades,
    );

    this.setShadowVariables(htmlEl, 'primary', primaryHSL);
  }
  hslModify(hsl, options: any = {}) {
    const { h = {}, s = {}, l = {}, opacity = 1 } = options;

    const modifiedHsl: any = { ...hsl };

    if (h.fixed !== undefined) modifiedHsl.h = h.fixed;
    if (h.percent !== undefined) modifiedHsl.h *= 1 + h.percent / 100;
    if (h.adjust !== undefined) modifiedHsl.h += h.adjust;

    if (s.fixed !== undefined) modifiedHsl.s = s.fixed;
    if (s.percent !== undefined) modifiedHsl.s *= 1 + s.percent / 100;
    if (s.adjust !== undefined) modifiedHsl.s += s.adjust;

    if (l.fixed !== undefined) modifiedHsl.l = l.fixed;
    if (l.percent !== undefined) modifiedHsl.l *= 1 + l.percent / 100;
    if (l.adjust !== undefined) modifiedHsl.l += l.adjust;

    if (l.l !== undefined) modifiedHsl.l = l.l;
    if (h.l !== undefined) modifiedHsl.l = h.l;
    if (s.l !== undefined) modifiedHsl.l = s.l;

    if (l.h !== undefined) modifiedHsl.h = l.h;
    if (h.h !== undefined) modifiedHsl.h = h.h;
    if (s.h !== undefined) modifiedHsl.h = s.h;

    if (l.s !== undefined) modifiedHsl.s = l.s;
    if (h.s !== undefined) modifiedHsl.s = h.s;
    if (s.s !== undefined) modifiedHsl.s = s.s;

    modifiedHsl.opacity = opacity;

    return modifiedHsl;
  }
  simpleh(hsl){
        const safeHSL = {
      h: hsl.h,
      s: hsl.s,
      l: hsl.l,
      opacity: hsl?.opacity ?? 1, // Use 1 if alpha is undefined or null
    };
    return `${safeHSL.h}`;
  }
   simples(hsl){
        const safeHSL = {
      h: hsl.h,
      s: hsl.s,
      l: hsl.l,
      opacity: hsl?.opacity ?? 1, // Use 1 if alpha is undefined or null
    };
    return `${safeHSL.s}%`;
  }
   simplel(hsl){
        const safeHSL = {
      h: hsl.h,
      s: hsl.s,
      l: hsl.l,
      opacity: hsl?.opacity ?? 1, // Use 1 if alpha is undefined or null
    };
    return `${safeHSL.l}%`;
  }
  finalHSL(hsl) {
    const safeHSL = {
      h: hsl.h,
      s: hsl.s,
      l: hsl.l,
      opacity: hsl?.opacity ?? 1, // Use 1 if alpha is undefined or null
    };

    return `hsl(${safeHSL.h}deg ${safeHSL.s}% ${safeHSL.l}% / ${safeHSL.opacity})`;
  }
}
